import React, { FC } from 'react';

import cn from 'classnames';

import { Avatar } from '@shared/ui/avatar';
import { Label } from '@shared/ui/label';
import IconMicOffColored from '@assets/img/icons/mic-off-colored-white.svg';
import IconPause from '@assets/img/icons/pause-circle.svg';
import IconPhoneCall from '@assets/img/icons/phone-call.svg';
import IconPhoneMissed from '@assets/img/icons/phone-missed.svg';

import { useCallsList, useRemoteSpeaking, useVideoBackdrop } from '../../hooks';
import { ConferenceGrid } from './conference-grid';
import { EMPTY_CAPTIONS_SRC, MAX_VISIBLE_CONFERENCE_USERS } from './constants';
import type { RemoteMediaStageProps } from './types';

import styles from './styles.module.scss';

const renderLabelContent = (name: string, isMuted?: boolean) => (
	<div className={styles.LabelInfo}>
		{isMuted && (
			<span className={styles.LabelIcon} dangerouslySetInnerHTML={{ __html: IconMicOffColored }} />
		)}
		{name}
	</div>
);

export const RemoteMediaStage: FC<RemoteMediaStageProps> = ({
	callStatus,
	contactName,
	state,
	isDisabledCallStatus,
	isRemoteMute,
	isRemoteVideoActive,
	remoteVideoRef,
}) => {
	const { activeConferenceUsers = [] } = useCallsList();
	const visibleParticipants = activeConferenceUsers.slice(0, MAX_VISIBLE_CONFERENCE_USERS);

	/** Флаг занятости/пропущенного вызова — влияет на иконки и цвета. */
	const isBusy = state === 'declined';
	/** Отмечает состояние звонка «calling» для запуска анимации аватара. */
	const isCalling = state === 'calling';

	const { remoteBackdropRef, remoteFrameRef, hasLetterbox } = useVideoBackdrop({
		remoteVideoRef,
		isRemoteVideoActive,
	});

	const isRemoteSpeaking = useRemoteSpeaking({
		remoteVideoRef,
		isRemoteMute,
	});

	return (
		<div className={styles.RemoteVideoContainer}>
			{isRemoteVideoActive && (
				<Label
					color="dark"
					tagClass={styles.LabelName}
					text={renderLabelContent(contactName, isRemoteMute)}
				/>
			)}
			<div
				ref={remoteFrameRef}
				className={cn(styles.RemoteVideoFrame, {
					[styles.WithBackdrop]: isRemoteVideoActive && hasLetterbox,
				})}
			>
				{/* Слой видео: фон + само видео; при выключении скрываем, но контейнер остаётся */}
				<div aria-hidden={!isRemoteVideoActive} className={styles.RemoteVideoLayer}>
					<video
						ref={remoteBackdropRef}
						autoPlay
						muted
						playsInline
						aria-hidden="true"
						className={cn(styles.RemoteVideoBackdrop, {
							[styles.Hide]: !isRemoteVideoActive || !hasLetterbox,
						})}
					/>
					<video
						ref={remoteVideoRef}
						autoPlay
						playsInline
						muted={false}
						className={cn(styles.VideoBlock, styles.RemoteVideo, {
							[styles.Hide]: !isRemoteVideoActive,
						})}
					>
						<track kind="captions" label="Live call stream" src={EMPTY_CAPTIONS_SRC} />
					</video>
				</div>
				{!isRemoteVideoActive &&
					(visibleParticipants.length > 0 ? (
						<ConferenceGrid
							participants={visibleParticipants}
							renderLabelContent={renderLabelContent}
						/>
					) : (
						// Аудио-фоллбек в том же фрейме, чтобы высота панели не прыгала
						<div className={cn(styles.CenterBlock, styles.AudioPlaceholder)}>
							<div className={styles.AudioBlock}>
								<Avatar
									name={contactName}
									size={120}
									className={cn(styles.AudioAvatar, {
										[styles.CallingAvatar]: isCalling,
										[styles.AudioAvatarSpeaking]: isRemoteSpeaking && !isCalling,
									})}
								/>
								<div
									className={cn(styles.Name, {
										[styles.RemoteMuteIcon]: isRemoteMute,
										[styles.DarkText]: true,
									})}
								>
									{contactName}
								</div>

								{callStatus && (
									<div className={cn(styles.Status, { [styles.DarkText]: true })}>
										<div
											className={cn(styles.StatusIcon, { [styles.busy]: isBusy })}
											dangerouslySetInnerHTML={{
												__html: isDisabledCallStatus
													? IconPause
													: isBusy
														? IconPhoneMissed
														: IconPhoneCall,
											}}
										/>
										<span>{callStatus}</span>
									</div>
								)}
							</div>
						</div>
					))}
			</div>
		</div>
	);
};
